import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useEffect} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback, ExternalFilters} from 'features/eag-grid';

import {ItemRowData} from '../types/ItemRowData';
import {MaterialWarehouseTabExternalFilters} from './MaterialWarehouseTabExternalFilters';

interface ReceiveNoteMaterialWarehouseTabProps extends RequiredTestIdProps {
  onAddToBasket: (itemRowData: ItemRowData) => void;
  onArticleDetail: (warehouseId: string, articleId: string) => void;
  onActiveFolderIdChange?: (activeFolderId?: string) => void;
  onExternalFilterChange: (filters: ExternalFilters) => void;
  hasPendingRequest: boolean;
}

const RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID = 'receive-note-basket-material-ext-filter';
const PREDEFINED_COLUMNS = [
  'id',
  'name',
  'warehouseId',
  'articleId',
  'catalogNumber',
  'manufacturerId',
] as const;

export function ReceiveNoteMaterialWarehouseTab(props: ReceiveNoteMaterialWarehouseTabProps) {
  const [folderTreeRef, dataGridModifier, activeFolderId] = useWarehouseDataGridTreeFolder();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  useEffect(
    function notifyActiveFolderIdChange() {
      props.onActiveFolderIdChange?.(activeFolderId);
    },
    [activeFolderId] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
    rowId,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const name = getColumnData<string>('name')?.value;
    const warehouseId = getColumnData<{id: string}>('warehouseId')?.value?.id;
    const articleId = getColumnData<string>('articleId')?.value;
    const manufacturerId = getColumnData<{id: string}>('manufacturerId')?.value?.id;
    const catalogNumber = getColumnData<string>('catalogNumber')?.value;

    if (isNil(name)) {
      return logError('name is not defined');
    }

    if (isNil(warehouseId)) {
      return logError('warehouseId is not defined');
    }

    if (isNil(articleId)) {
      return logError('articleId is not defined');
    }

    if (isNil(catalogNumber)) {
      return logError('catalogNumber is not defined');
    }

    if (isNil(manufacturerId)) {
      return logError('manufacturerId is not defined');
    }

    const articleRowData: ItemRowData = {
      warehouseId,
      articleId,
      catalogNumber,
      name,
      manufacturerId,
      itemId: rowId as string,
    };

    match(actionKey)
      .with('toBasket', () => props.onAddToBasket(articleRowData))
      .with('articleDetail', () => props.onArticleDetail(warehouseId, articleId))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };
  return (
    <FullscreenDataGridWrapper
      externalFilterId={RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID}
      folderTreeRef={folderTreeRef}
      hideCardWrapper
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        // DG must be re-rendered on folder change to update the query modifier
        // eslint-disable-next-line eslint-js/no-restricted-syntax
        key={dataGridModifier?.key}
        gridCode={WAREHOUSE_GRID_CODES.receiveNoteBasketArticle}
        autoFocusSmartSearch
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        externalFilterId={RECEIVE_NOTE_MATERIAL_EXTERNAL_FILTER_ID}
        onFilterChanged={props.onExternalFilterChange as (filters: unknown) => void}
        filterComponent={MaterialWarehouseTabExternalFilters}
        queryModifier={dataGridModifier?.queryModifier}
        data-testid={WAREHOUSE_GRID_CODES.receiveNoteBasketArticle}
      />
    </FullscreenDataGridWrapper>
  );
}
